import { Link, Typography } from '@material-ui/core';
import {
  CheckboxField,
  ColorDynamic,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useEffect, useRef, useState } from 'react';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { useSettleInstantTransfer } from 'shared/modules/superpay/SuperPayAPI';
import { InstantTransferSettleDialogDTO } from 'shared/modules/superpay/SuperPayDTO';
import styled from 'styled-components';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import {
  extractBankLastFourDigits,
  InstantTransferRecoverySettleSource,
  trackInstantTransferRecoverySettleConfirmed,
  trackInstantTransferRecoverySettleDialogCanceled,
  trackInstantTransferRecoverySettleDialogOpened,
} from './InstantTransferRecoveryAnalytics';
import { InstantTransferSettleCompleted } from './InstantTransferSettleCompleted';
import { isWebView } from 'shared/constants/AppTypeConstants';

const InfoBox = styled(Box)`
  background-color: ${ColorDynamic.Silver200};
  border-radius: 4px;
  padding: 8px;
  display: grid;
  grid-template-columns: 1fr 62%;
  grid-template-rows: 1fr 1fr 1fr;
`;

const ActionWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

interface InstantTransferSettleDialogContentProps {
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  settleInformation?: InstantTransferSettleDialogDTO;
  source?: InstantTransferRecoverySettleSource;
  isOpen?: boolean;
}

export function InstantTransferSettleDialogContent({
  onClose,
  onSuccess,
  title = 'Settle Instant Transfer Advance',
  settleInformation,
  source = 'Load Details',
  isOpen = true,
}: InstantTransferSettleDialogContentProps) {
  const { addSnackbar } = useSnackbarStack();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (isOpen && settleInformation && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      const firstLoad = settleInformation.loads?.[0];
      trackInstantTransferRecoverySettleDialogOpened({
        source,
        order_id: firstLoad?.load_id ?? '',
      });
    }

    if (!isOpen) {
      hasTrackedRef.current = false;
    }
  }, [isOpen, settleInformation, source]);

  const load = settleInformation?.loads?.[0];

  const { mutate: payNow, isPending: isSettling } = useSettleInstantTransfer({
    onSuccess: () => {
      addSnackbar('Settlement Funds Submitted successfully.', {
        variant: 'success',
      });
      onSuccess?.();
      onClose();
    },
    onError: () => {
      addSnackbar('Something went wrong. Please try again.', {
        variant: 'error',
      });
    },
  });

  if (settleInformation && !settleInformation.status) {
    return (
      <Box width={['100%', '100%', '505px']}>
        <InstantTransferSettleCompleted
          onClose={onClose}
          loadID={load?.load_id}
        />
      </Box>
    );
  }

  return (
    <Box width={['100%', '100%', '624px']}>
      <InstantTransferSettleForm
        title={title}
        settleInformation={settleInformation}
        onClose={onClose}
        onPayNow={() => {
          if (load?.load_guid) {
            payNow([load?.load_guid]);
          }
        }}
        isSettling={isSettling}
        source={source}
      />
    </Box>
  );
}

function ActionButton({
  status,
  disabled,
  onPayNow,
}: {
  status?: InstantTransferSettleDialogDTO['status'];
  disabled: boolean;
  onPayNow: () => void;
}) {
  const { showLauncherMessanger } = useCustomerSupport();

  return status === 'failed' ? (
    <Button fullWidth={isWebView} onClick={() => showLauncherMessanger()}>
      Contact Support
    </Button>
  ) : (
    <Button fullWidth={isWebView} disabled={disabled} onClick={onPayNow}>
      Pay Now
    </Button>
  );
}

interface InstantTransferSettleFormProps {
  settleInformation?: InstantTransferSettleDialogDTO;
  onClose: () => void;
  onPayNow: () => void;
  isSettling: boolean;
  title: string;
  source?: InstantTransferRecoverySettleSource;
}

function InstantTransferSettleForm({
  settleInformation,
  onClose,
  onPayNow,
  isSettling,
  title,
  source = 'Load Details',
}: InstantTransferSettleFormProps) {
  const [isTermsAgree, setTermsAgree] = useState(false);
  const load = settleInformation?.loads?.[0];
  const isFailed = settleInformation?.status === 'failed';
  const isAccessRestricted =
    settleInformation?.status === 'past_due' ||
    settleInformation?.status === 'failed';
  const formattedAmount = formatCurrency(load?.price, {
    minimumFractionDigits: 0,
  });

  return (
    <Box padding="small">
      <Stack space="small">
        <Stack space="small">
          <Typography variant="h3">{title}</Typography>
          <Stack>
            {isAccessRestricted && (
              <Typography color="error">
                Your Instant Transfer access has been restricted due to an
                outstanding payments.
              </Typography>
            )}
            {isFailed ? (
              <Typography>
                Your last payment failed. Contact Support to settle your balance
                and regain access to Instant Transfer.
              </Typography>
            ) : (
              <Typography>
                Pay now to settle the Instant Transfer advance for this order.{' '}
                <b>We'll debit the bank account listed below.</b>
              </Typography>
            )}
          </Stack>
        </Stack>
        <InfoBox>
          <Box padding="xsmall">
            <Typography>Amount</Typography>
          </Box>
          <Box padding="xsmall">
            <Typography>{formattedAmount}</Typography>
          </Box>
          <Box padding="xsmall">
            <Typography>Bank Details</Typography>
          </Box>
          <Box padding="xsmall">
            <Typography>{settleInformation?.bank_account}</Typography>
          </Box>
          <Box padding="xsmall">
            <Typography>Load ID</Typography>
          </Box>
          <Box padding="xsmall">
            <Typography>
              {isWebView ? (
                load?.load_id
              ) : (
                <Link
                  underline="none"
                  color="primary"
                  href={`/tms/loads/${load?.load_guid}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {load?.load_id}
                </Link>
              )}
            </Typography>
          </Box>
        </InfoBox>

        {settleInformation?.status !== 'failed' && (
          <CheckboxField
            checked={isTermsAgree}
            onChange={(_, value) => {
              setTermsAgree(value);
            }}
            label={
              <Typography>
                I confirm sufficient funds are available, I understand that in
                case of payment issue access to Instant Transfer program may be
                lost.
              </Typography>
            }
          />
        )}

        <ActionWrapper>
          <Button
            fullWidth={isWebView}
            variant="neutral"
            onClick={() => {
              const firstLoad = settleInformation?.loads?.[0];
              trackInstantTransferRecoverySettleDialogCanceled({
                source,
                order_id: firstLoad?.load_id ?? '',
                checkbox_was_checked: isTermsAgree,
              });
              onClose();
            }}
            disabled={isSettling}
          >
            No, I'll Pay Later
          </Button>
          <ActionButton
            onPayNow={() => {
              const firstLoad = settleInformation?.loads?.[0];
              trackInstantTransferRecoverySettleConfirmed({
                source,
                order_id: firstLoad?.load_id ?? '',
                bank_last4: extractBankLastFourDigits(
                  settleInformation?.bank_account,
                ),
              });
              onPayNow();
            }}
            status={settleInformation?.status}
            disabled={!isTermsAgree || isSettling}
          />
        </ActionWrapper>
      </Stack>
    </Box>
  );
}
